import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Icon } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { CarExternalDataModal, CarSearch } from "modules/Appointments/components/NewAppointment/components/CarSearchFormSection/components";
import { canWriteBackCustomerData } from "utils/appointmentUtils";

import type { AppointmentSection, HandleCustomerResultSelectParams, SEARCH_TAGS } from "modules/Appointments/components/NewAppointment";

type CarSearchFormSectionProps = {
  carSearchTerm: string;
  lastSelectedTitle: string;
  showCarTag: SEARCH_TAGS | null;
  handleOpenCarMileageModal: () => void;
  handleSetCarSearchTerm: (value: string) => void;
  handleSetLastSelectedTitle: (value: string) => void;
  handleSetShowCarTag: (value: SEARCH_TAGS | null) => void;
  handleSetCarFromExternalData: (value: boolean) => void;
  handleSetDisabledOwnerSearch: (value: boolean) => void;
  handleSetDisabledDriverSearch: (value: boolean) => void;
  handleSetDisabledContractorSearch: (value: boolean) => void;
  handleSetCustomerOwnerPreselect: (value: boolean) => void;
  handleSetCustomerDriverPreselect: (value: boolean) => void;
  handleSetCustomerContractorPreselect: (value: boolean) => void;
  handleCustomerResultSelect: ({ customer, customerType }: HandleCustomerResultSelectParams) => void;
};

const FormHelper = createFormHelper<AppointmentSection>();

export const CarSearchFormSection = ({
  showCarTag,
  carSearchTerm,
  lastSelectedTitle,
  handleOpenCarMileageModal,
  handleSetCustomerOwnerPreselect,
  handleSetCustomerDriverPreselect,
  handleSetCustomerContractorPreselect,
  handleSetDisabledOwnerSearch,
  handleSetDisabledDriverSearch,
  handleSetDisabledContractorSearch,
  handleSetShowCarTag,
  handleSetCarSearchTerm,
  handleSetLastSelectedTitle,
  handleSetCarFromExternalData,
  handleCustomerResultSelect
}: CarSearchFormSectionProps) => {
  const { t } = useTranslation();

  const [showCarExternalDataModal, setShowCarExternalDataModal] = useState(false);
  const { selectedLocation } = useSelectedLocation();
  const { setValue, watch } = FormHelper.useFormContext();
  const [id, vin_nr, reg_nr, make, model, apk_date_dms, dms_nr] = watch([
    "car.id",
    "car.vin_nr",
    "car.reg_nr",
    "car.make",
    "car.model",
    "car.apk_date_dms",
    "car.dms_nr"
  ]);

  const hasWriteBackPermissionForCustomerData = canWriteBackCustomerData(selectedLocation);

  const handleCarInShopToggle = (checked: boolean) => {
    setValue("car_in_shop", checked);
    hasWriteBackPermissionForCustomerData && handleOpenCarMileageModal();
  };

  return (
    <>
      <Form.Group className="NewAppointment__GrayGroup">
        <Form.Field className="dropdown-search" width={6}>
          <label className="big">
            <Icon className="car mirrors" />
            {t("v8_car", "Car")}
          </label>
          <div className="search">
            <CarSearch
              showCarTag={showCarTag}
              carSearchTerm={carSearchTerm}
              lastSelectedTitle={lastSelectedTitle}
              handleSetShowCarTag={handleSetShowCarTag}
              handleSetCarSearchTerm={handleSetCarSearchTerm}
              handleCustomerResultSelect={handleCustomerResultSelect}
              handleSetCarFromExternalData={handleSetCarFromExternalData}
              handleSetLastSelectedTitle={handleSetLastSelectedTitle}
              handleSetCustomerOwnerPreselect={handleSetCustomerOwnerPreselect}
              handleSetCustomerDriverPreselect={handleSetCustomerDriverPreselect}
              handleSetCustomerContractorPreselect={handleSetCustomerContractorPreselect}
              handleSetDisabledOwnerSearch={handleSetDisabledOwnerSearch}
              handleSetDisabledDriverSearch={handleSetDisabledDriverSearch}
              handleSetDisabledContractorSearch={handleSetDisabledContractorSearch}
            />
            <Form.Button type="button" disabled={!showCarTag && reg_nr === "" && !carSearchTerm} onClick={() => setShowCarExternalDataModal(true)}>
              {t("v8_external_data", "External Data")}
            </Form.Button>
            <div className="appointment-info-checkbox-container">
              <div className="car-in-shop-toggle">
                <div>
                  <Icon className="garage car" />
                  <span>{t("v8_car_in_shop", "Car in Shop")}</span>
                </div>
                <FormHelper.Field.Checkbox name="car_in_shop" onChange={checked => handleCarInShopToggle(checked)} />
              </div>
              <div className="customer-waiting-toggle">
                <div>
                  <Icon className="mug saucer CustomerWaitingIcon" />
                  <span>{t("v8_customer_waiting", "Customer waiting")}</span>
                </div>
                <FormHelper.Field.Checkbox name="customer_waiting" />
              </div>
              {!hasWriteBackPermissionForCustomerData && (
                <>
                  <div>
                    <div>
                      <Icon className="hotel" />
                      <span>{t("v8_lease_company", "Lease company")}</span>
                    </div>
                    <FormHelper.Field.Checkbox name="is_lease_company" />
                  </div>
                  <div>
                    <div>
                      <Icon className="user slash" />
                      <span>{t("v8_internal", "Internal")}</span>
                    </div>
                    <FormHelper.Field.Checkbox name="internal" />
                  </div>
                </>
              )}
            </div>
          </div>
        </Form.Field>
      </Form.Group>
      <CarExternalDataModal
        open={showCarExternalDataModal}
        carFormValues={{ id, vin_nr, reg_nr, make, model, apk_date_dms, dms_nr }}
        carSearchTerm={carSearchTerm}
        onClose={() => setShowCarExternalDataModal(false)}
        handleSetCarFromExternalData={handleSetCarFromExternalData}
      />
    </>
  );
};
